import React, { useMemo } from "react";
import { HiPlus, HiTrash } from "react-icons/hi2";
import SessionCard from "./SessionCard";
import EventCard from "./EventCard";
import { SessionVM } from "./scheduleTypes";
import {
  compareSessionsWithinDay,
  getDayGroupLabel,
  getDayGroupSubLabel,
  groupSessionsByDay,
  DayGroup,
} from "./scheduleGrouping";
import { EventVM, eventsByDateKey } from "./eventVM";
import RecoveryScheduleCard from "./RecoveryScheduleCard";
import { ScheduledRecovery } from "../../types/scheduledRecovery";
import { getLocalDateString } from "./scheduleDateUtils";
import { ColorMap } from "../../utils/scheduleColors";
import { TEXT_BUTTON_CLASS } from "../../utils/buttonStyles";

interface ListViewProps {
  sessions: SessionVM[];
  /** Upcoming events (Events.md §4), expanded per day. */
  events?: EventVM[];
  isEventLogged?: (eventId: string) => boolean;
  onSelectEvent?: (eventId: string) => void;
  recoveries?: ScheduledRecovery[];
  onSelectRecovery?: (item: ScheduledRecovery) => void;
  trainingTypeColors?: ColorMap;
  hasMore: boolean;
  onLoadMore: () => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  /** Present when the session may move (see `canChangeSessionDate`). */
  canChangeDate: (id: string) => boolean;
  onChangeDate: (id: string) => void;
  onPlanSession: () => void;
  // Bulk select
  bulkSelectMode: boolean;
  onToggleBulkSelect: () => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onDeleteSelected: () => void;
}

/**
 * Upcoming sessions grouped by day (Today, Tomorrow, weekday + date), plan
 * 3d. Tapping a card hands the id to the parent, which opens the session
 * bottom sheet; "Change date" is also available on the card itself.
 */
const ListView: React.FC<ListViewProps> = ({
  sessions,
  events = [],
  isEventLogged,
  onSelectEvent,
  recoveries = [],
  onSelectRecovery,
  trainingTypeColors,
  hasMore,
  onLoadMore,
  onSelectSession,
  onDeleteSession,
  canChangeDate,
  onChangeDate,
  onPlanSession,
  bulkSelectMode,
  onToggleBulkSelect,
  selectedIds,
  onToggleSelect,
  onDeleteSelected,
}) => {
  // Events on each upcoming day (a multi-day event appears on every day).
  const eventsByDay = useMemo(() => {
    const todayKey = getLocalDateString(new Date());
    const map = eventsByDateKey(events);
    Array.from(map.keys()).forEach((key) => {
      if (key < todayKey) map.delete(key);
    });
    return map;
  }, [events]);

  const groups = useMemo(() => {
    const dayGroups: DayGroup<SessionVM>[] = groupSessionsByDay(sessions);
    for (const group of dayGroups) {
      group.sessions.sort(compareSessionsWithinDay);
    }
    // Days that only hold a event still get a group.
    const today = new Date();
    const todayKey = getLocalDateString(today);
    const known = new Set(dayGroups.map((g) => g.date));
    const upcomingRecoveries = recoveries.filter((item) => item.plannedDate >= todayKey);
    upcomingRecoveries.forEach((item) => {
      if (known.has(item.plannedDate)) return;
      dayGroups.push({
        date: item.plannedDate,
        label: getDayGroupLabel(item.plannedDate, today),
        subLabel: getDayGroupSubLabel(item.plannedDate, today),
        isToday: item.plannedDate === todayKey,
        sessions: [],
      });
      known.add(item.plannedDate);
    });
    eventsByDay.forEach((_, date) => {
      if (known.has(date)) return;
      dayGroups.push({
        date,
        label: getDayGroupLabel(date, today),
        subLabel: getDayGroupSubLabel(date, today),
        isToday: date === todayKey,
        sessions: [],
      });
    });
    return dayGroups.sort((a, b) => a.date.localeCompare(b.date));
  }, [sessions, eventsByDay, recoveries]);

  const recoveriesByDay = useMemo(() => {
    const todayKey = getLocalDateString(new Date());
    const map = new Map<string, ScheduledRecovery[]>();
    for (const item of recoveries) {
      if (item.plannedDate < todayKey) continue;
      const list = map.get(item.plannedDate) ?? [];
      list.push(item);
      map.set(item.plannedDate, list);
    }
    return map;
  }, [recoveries]);

  if (sessions.length === 0 && eventsByDay.size === 0 && recoveriesByDay.size === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-base-300 bg-base-100 p-10 text-center">
        <h3 className="text-lg font-semibold text-base-content font-heading">
          Nothing scheduled
        </h3>
        <p className="mt-2 text-sm text-base-content/60 font-grotesk max-w-sm mx-auto">
          Plan a session or wait for your coach to assign one; upcoming
          sessions appear here grouped by day.
        </p>
        <div className="mt-5 flex justify-center">
          <button
            onClick={onPlanSession}
            className="btn btn-neutral min-h-11 h-11 rounded-xl px-5 text-sm font-medium font-grotesk normal-case"
          >
            <HiPlus className="w-4 h-4" />
            Schedule Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-base-content/40 font-grotesk">
          Upcoming
        </span>
        <button
          onClick={onToggleBulkSelect}
          className={`btn btn-ghost btn-sm min-h-11 h-11 -mr-3 rounded-xl px-3 text-sm font-medium font-grotesk normal-case transition-colors ${
            bulkSelectMode ? "text-error" : "text-primary hover:text-primary/80"
          }`}
        >
          {bulkSelectMode ? "Cancel" : "Select"}
        </button>
      </div>

      {bulkSelectMode && selectedIds.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-xl bg-base-100 border border-base-300 p-3">
          <span className="text-sm font-grotesk text-base-content">
            <span className="font-geist tabular-nums">{selectedIds.size}</span>{" "}
            selected
          </span>
          <button
            onClick={onDeleteSelected}
            className="btn btn-error btn-sm min-h-11 h-11 rounded-xl px-4 text-sm font-medium font-grotesk normal-case"
          >
            <HiTrash className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      <div className="space-y-5">
        {groups.map((group) => (
          <section key={group.date} aria-label={group.label}>
            <div className="mb-2 flex items-baseline gap-2">
              <h3
                className={`text-sm font-semibold font-grotesk ${
                  group.isToday ? "text-primary" : "text-base-content"
                }`}
              >
                {group.label}
              </h3>
              {group.subLabel && (
                <span className="text-xs text-base-content/50 font-geist">
                  {group.subLabel}
                </span>
              )}
              <span className="ml-auto text-xs text-base-content/40 font-geist tabular-nums">
                {group.sessions.length +
                  (eventsByDay.get(group.date)?.length ?? 0) +
                  (recoveriesByDay.get(group.date)?.length ?? 0)}
              </span>
            </div>
            <div className="space-y-2">
              {(recoveriesByDay.get(group.date) ?? []).map((item) => (
                <RecoveryScheduleCard
                  key={item.id}
                  item={item}
                  onClick={
                    onSelectRecovery && !bulkSelectMode
                      ? () => onSelectRecovery(item)
                      : undefined
                  }
                />
              ))}
              {(eventsByDay.get(group.date) ?? []).map((comp) => (
                <EventCard
                  key={`comp-${comp.id}-${group.date}`}
                  event={comp}
                  dateKey={group.date}
                  variant="compact"
                  logged={isEventLogged?.(comp.id) ?? false}
                  onClick={
                    onSelectEvent && !bulkSelectMode
                      ? () => onSelectEvent(comp.id)
                      : undefined
                  }
                />
              ))}
              {group.sessions.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  trainingTypeColors={trainingTypeColors}
                  variant="compact"
                  selectable={bulkSelectMode}
                  selected={selectedIds.has(session.id)}
                  onToggleSelect={() => onToggleSelect(session.id)}
                  onClick={() =>
                    bulkSelectMode
                      ? onToggleSelect(session.id)
                      : onSelectSession(session.id)
                  }
                  onDelete={() => onDeleteSession(session.id)}
                  onChangeDate={
                    canChangeDate(session.id)
                      ? () => onChangeDate(session.id)
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={onLoadMore}
          className={`${TEXT_BUTTON_CLASS} mt-4 w-full`}
        >
          Load more
        </button>
      )}
    </div>
  );
};

export default ListView;
